import { MarkdownContent } from 'elements/visual/MarkdownContent';
import { GStory_cards_LinkedStoriesCard } from 'graphql/fragment/__generated__/GStory';
import { EColors2, EFonts } from 'libs/styles/styling';
import { useAnalytics } from 'libs/utils/site/analytics';
import { useContext } from 'react';
import { useNavigate } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import { CtxEventView } from 'session/ctxEventView';
import styled from 'styled-components/macro';

interface Props {
    className?: string;
    card: GStory_cards_LinkedStoriesCard;
}

export const LinkedDetailsCard = (props: Props): JSX.Element => {
    const ctxEventView = useContext(CtxEventView);
    const [searchParams, setSearchParams] = useSearchParams();
    const analytics = useAnalytics();
    const navigate = useNavigate();

    const card = props.card;

    if (!ctxEventView) {
        return null;
    }

    function openDetail(storyId: string) {
        analytics.track('event_ui.detail.open_clicked', {
            type: 'card',
            storyId: storyId,
            title: ctxEventView.allStories.find((e) => e.storyId === storyId)?.title
        });

        if (!ctxEventView.isWide) {
            const mobilePath = `/event/${ctxEventView.eventPath}/detail/${storyId}`;
            navigate(mobilePath);
        } else {
            searchParams.set('detailId', storyId);
            setSearchParams(searchParams);
        }
    }

    const stories = card.linkedStories?.storyIds
        ?.map((storyId) => ctxEventView.allStories.find((p) => p.storyId === storyId))
        .filter(Boolean);

    return (
        <S_LinkedDetailsCard className={props.className}>
            {card && <S_Title>{card.info?.title}</S_Title>}
            <S_List>
                {stories.map((story) => {
                    return (
                        <S_Detail
                            key={story.storyId}
                            onClick={() => {
                                openDetail(story.storyId);
                            }}
                        >
                            <S_ImageContainer>
                                <S_Image $backgroundImageUrl={story.storyHeader?.imageUrl} />
                            </S_ImageContainer>

                            <S_Content>
                                <S_DetailTitle>{story.storyHeader?.title}</S_DetailTitle>
                                <S_DetailDescription text={story.storyHeader?.text} />
                            </S_Content>
                        </S_Detail>
                    );
                })}
            </S_List>
        </S_LinkedDetailsCard>
    );
};

const S_LinkedDetailsCard = styled.div`
    display: flex;
    flex-direction: column;
`;

const S_Title = styled.div`
    font-size: 18px;
    line-height: 20px;
    color: ${EColors2.DARK};
    font-family: ${EFonts.Bold};
    margin-bottom: 20px;
`;

const S_List = styled.div`
    box-shadow: 3px 3px 15px 0px rgba(0, 0, 0, 0.05);
    display: flex;
    flex-direction: column;
    border-radius: 15px;
`;

const S_ImageContainer = styled.div`
    width: 105px;
    height: 105px;
    overflow: hidden;
    flex-grow: 0;
    flex-shrink: 0;
`;

const S_Detail = styled.div`
    display: flex;
    flex-direction: row;
    background: #fff;
    border-bottom: 1px solid #f2f2f2;
    cursor: pointer;
    &:first-child {
        border-top-left-radius: 15px;
        border-top-right-radius: 15px;
        ${S_ImageContainer} {
            border-top-left-radius: 15px;
        }
    }
    &:last-child {
        border-bottom-left-radius: 15px;
        border-bottom-right-radius: 15px;
        ${S_ImageContainer} {
            border-bottom-left-radius: 15px;
        }
        border-bottom: none;
    }
`;

const S_Image = styled.div<{ $backgroundImageUrl: string }>`
    background-image: url('${(props) => props.$backgroundImageUrl}');
    background-repeat: no-repeat;
    background-size: cover;
    background-position: center;
    width: 100%;
    height: 100%;
`;
const S_Content = styled.div`
    display: flex;
    flex-direction: column;
    padding: 10px 20px 0 20px;
`;
const S_DetailTitle = styled.div`
    color: #041321;
    font-family: ${EFonts.Bold};
    font-size: 15px;
    margin-bottom: 9px;
`;

const S_DetailDescription = styled(MarkdownContent)`
    color: #4a4a4a;
    font-family: ${EFonts.Regular};
    font-size: 14px;
    font-style: normal;
    font-weight: 600;
    // BEIN Ellipsis
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    // END Ellipsis
    line-height: 22px;
`;
